import { useEffect, useRef, useState } from "react";

/**
 * Returns whether the user has scrolled past one viewport height — a
 * stable snapshot of `window.innerHeight` captured at mount, *not* the
 * live value. Used as the "approximately past the hero" trigger by
 * both the reveal-on-scroll mobile sticky CTA panel and the
 * scroll-controlled nav visibility hook.
 */

const REFERENCE_RESIZE_THRESHOLD_PX = 150;

type UseScrolledPastViewportOptions = {
  enabled?: boolean;
};

function isTextEntryElement(element: Element | null): boolean {
  if (!element) {
    return false;
  }

  const tagName = element.tagName.toLowerCase();
  return (
    tagName === "input" ||
    tagName === "textarea" ||
    element.getAttribute("contenteditable") === "true"
  );
}

function useScrolledPastViewport({
  enabled = true,
}: UseScrolledPastViewportOptions = {}): boolean {
  const [hasScrolledPast, setHasScrolledPast] = useState(false);
  const referenceHeightRef = useRef<number | null>(null);
  const referenceWidthRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    referenceHeightRef.current = window.innerHeight;
    referenceWidthRef.current = window.innerWidth;

    const check = () => {
      const reference = referenceHeightRef.current ?? window.innerHeight;
      const next = window.scrollY >= reference;
      setHasScrolledPast((prev) => (prev === next ? prev : next));
    };

    const refreshReferenceOnRealResize = () => {
      const currentHeight = window.innerHeight;
      const currentWidth = window.innerWidth;
      const referenceHeight = referenceHeightRef.current ?? currentHeight;
      const referenceWidth = referenceWidthRef.current ?? currentWidth;
      const heightChangedEnough =
        Math.abs(currentHeight - referenceHeight) >
        REFERENCE_RESIZE_THRESHOLD_PX;
      const widthChanged = currentWidth !== referenceWidth;

      if (!heightChangedEnough) {
        return;
      }

      if (!widthChanged && isTextEntryElement(document.activeElement)) {
        return;
      }

      referenceHeightRef.current = currentHeight;
      referenceWidthRef.current = currentWidth;
      check();
    };

    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", refreshReferenceOnRealResize, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", refreshReferenceOnRealResize);
    };
  }, [enabled]);

  return enabled && hasScrolledPast;
}

export { useScrolledPastViewport };
